import FormControl, { type FormControlProps } from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Switch, { type SwitchProps } from '@mui/material/Switch';
import { type JSX, type ReactNode, useCallback } from 'react';

import { type Control, type FieldPath, type FieldValues, useController } from 'react-hook-form';
import { DesignSystemScope } from '../theme/DesignSystemScope';
import { useDesignSystemEnabled } from '../theme/useDesignSystemEnabled';
import { SWITCH_FIELD_DESIGN_SYSTEM_KEYS } from './switchFieldDesignSystem';

export type MuiSwitchProps = SwitchProps & { 'data-test'?: string };

export type RHFMuiSwitchProps<T extends FieldValues> = Omit<MuiSwitchProps, 'name'> & {
  control?: Control<T>;
  hideValidationErrors?: boolean;
  label: ReactNode;
  margin?: FormControlProps['margin'];
  name: FieldPath<T>;
  /** Forces the design-system (`true`) or legacy (`false`) version; defaults to the `ui-design-system` GrowthBook feature. */
  designSystem?: boolean;
};

export function RHFSwitchField<T extends Record<string, unknown> = Record<string, string>>({
  control,
  hideValidationErrors = false,
  label,
  margin,
  name,
  designSystem,
  ...rest
}: RHFMuiSwitchProps<T>): JSX.Element {
  const isDesignSystem = useDesignSystemEnabled('switchField', designSystem);
  const {
    field,
    fieldState,
    formState: { isSubmitting },
  } = useController({
    control,
    name,
  });

  const defaultProps: MuiSwitchProps = {
    color: 'primary',
    disableRipple: true,
  };

  const helperText = fieldState.error?.message;
  const parentDataTest = rest['data-test'];
  rest['data-test'] = `${rest['data-test']}-switch`;

  const handleChange = useCallback<Required<SwitchProps>['onChange']>(
    (event, value) => {
      rest.onChange?.(event, value);
      if (event.isDefaultPrevented()) {
        return;
      }

      field.onChange(value);
    },
    [rest.onChange, field.onChange]
  );

  const switchField = (
    <FormControl error={fieldState.invalid} data-test={parentDataTest} margin={margin}>
      <FormControlLabel
        control={
          <Switch
            {...defaultProps}
            {...rest}
            {...field}
            checked={rest.checked ?? !!field.value}
            disabled={isSubmitting || rest.disabled}
            onChange={handleChange}
          />
        }
        label={label}
        data-test={`${parentDataTest}-label`}
      />
      {!hideValidationErrors && helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );

  return isDesignSystem ? (
    <DesignSystemScope keys={SWITCH_FIELD_DESIGN_SYSTEM_KEYS}>{switchField}</DesignSystemScope>
  ) : (
    switchField
  );
}
